import type { ArtifactKind } from './types'
/**
 * Junta os eventos `artifact_*` do SSE ate o painel ficar inteiro.
 *
 * ═══ POR QUE UM COLETOR, E NAO UM `case` MAIOR NO SWITCH ═══
 *
 * O painel chega picado: `artifact_start`, N x `artifact_delta`, `artifact_end`.
 * Nada pode ser desenhado antes do `end` — um JSON pela metade nao e um painel
 * incompleto, e um JSON invalido. Guardar esse estado dentro do `switch` do
 * `chat-stream.ts` misturaria o ciclo de vida do painel com o do turno, e o
 * tornaria impossivel de testar sem rede.
 *
 * ═══ O QUE ESTE ARQUIVO DEFENDE ═══
 *
 * O `switch` que le SSE nesta tela NAO tem `default`: evento desconhecido,
 * fora de ordem ou orfao era descartado SEM ruido — sem erro, sem log, sem
 * sintoma. Era assim que os cinco eventos de painel do motor sumiam. Aqui,
 * `delta` sem `start` e `end` sem `start` viram RECUSA explicita, entregue ao
 * chamador; e um `spec` que nao vira JSON derruba o PAINEL, nunca a conversa.
 */

export interface CabecalhoDeArtifact {
  id: string
  /**
   * DERIVADO de `types.ts`, e nao redigitado. A uniao existia aqui de novo, e
   * eram duas listas que precisavam concordar sempre: acrescentar um kind num
   * lugar e esquecer o outro daria uma tela que ACEITA o artifact no coletor e
   * o RECUSA no tipo (ou o contrario), em silencio. O motor e a fonte; aqui ha
   * um espelho so.
   */
  kind: ArtifactKind
  title: string
  version: number
}

export interface ArtifactPronto {
  id: string
  kind: CabecalhoDeArtifact['kind']
  title: string
  version: number
  /** O `DashboardSpec` que o motor JA validou. A tela nunca monta spec. */
  spec: unknown
}

export type FalhaDeArtifact =
  | { motivo: 'delta_sem_start'; id: string }
  | { motivo: 'end_sem_start'; id: string }
  | { motivo: 'spec_ilegivel'; id: string; detalhe: string }

interface EmMontagem {
  cabecalho: CabecalhoDeArtifact
  pedacos: string[]
}

export class ColetorDeArtifacts {
  private readonly abertos = new Map<string, EmMontagem>()

  start(cabecalho: CabecalhoDeArtifact): void {
    this.abertos.set(cabecalho.id, { cabecalho, pedacos: [] })
  }

  /** Devolve a falha quando o pedaco nao pertence a nenhum painel aberto. */
  delta(id: string, patch: string): FalhaDeArtifact | null {
    const emMontagem = this.abertos.get(id)
    if (!emMontagem) return { motivo: 'delta_sem_start', id }
    emMontagem.pedacos.push(patch)
    return null
  }

  /**
   * Fecha o painel. Devolve o painel pronto, ou a falha — nunca `undefined`
   * silencioso, que e como os eventos sumiam antes.
   */
  end(id: string): { ok: true; artifact: ArtifactPronto } | { ok: false; falha: FalhaDeArtifact } {
    const emMontagem = this.abertos.get(id)
    if (!emMontagem) return { ok: false, falha: { motivo: 'end_sem_start', id } }
    this.abertos.delete(id)

    try {
      const spec: unknown = JSON.parse(emMontagem.pedacos.join(''))
      return {
        ok: true,
        artifact: {
          id: emMontagem.cabecalho.id,
          kind: emMontagem.cabecalho.kind,
          title: emMontagem.cabecalho.title,
          version: emMontagem.cabecalho.version,
          spec,
        },
      }
    } catch (erro) {
      // O painel morre; a conversa nao. Uma excecao solta aqui subiria pelo
      // laco de leitura do SSE e mataria o turno inteiro por causa de um
      // grafico — trocar a resposta do usuario por uma tela branca e pior do
      // que entregar a resposta sem o painel.
      return {
        ok: false,
        falha: {
          motivo: 'spec_ilegivel',
          id,
          detalhe: erro instanceof Error ? erro.message : String(erro),
        },
      }
    }
  }

  /** Ids abertos e nunca fechados — o turno acabou no meio do painel. */
  abertosPendentes(): string[] {
    return [...this.abertos.keys()]
  }
}
